import { createSelector } from 'reselect';
import createMetadataAPISelectors from '../../utils/api-metadata-selectors';
import { sortBy } from 'lodash';

const options = {
  name: 'dashboards',
  baseType: 'DASHBOARDS'
};

const metadataApiSelectors = createMetadataAPISelectors(options);

const selectCurrentDashboardId = state => state.dashboards.currentDashboardId;

const emptyDashboard = { title: '', pages: [{ widgets: [] }] };

const allGroups = state => state.groups.byId;

export const selectCurrentDashboard = createSelector(
  metadataApiSelectors.byId,
  selectCurrentDashboardId,
  (byId, currentDashboardId) => (currentDashboardId && byId[currentDashboardId]) || emptyDashboard
);

export const selectIsDashboardMode = createSelector(
  state => state.dashboards.dashboardMode,
  dashboardMode => dashboardMode
);

export const selectDashboardDatetime = state => state.dashboards.dashboardDatetime;
const selectActivePageIndex = state => state.dashboards.activePageIndex;

export const selectActivePage = createSelector(
  selectCurrentDashboard,
  selectActivePageIndex,
  (dashboard, activePageIndex) => dashboard.pages[activePageIndex]
);

export const ignoredActivityTypes = createSelector(
  selectCurrentDashboard,
  dashboard => dashboard.incident_ignored_activity_type_ids || []
);

export const ignoredTaskIds = createSelector(
  selectCurrentDashboard,
  dashboard => dashboard.incident_ignored_task_ids || []
);

const selectCurrentDashboardGroupId = createSelector(selectCurrentDashboard, dashboard => dashboard.group_id);

const selectCurrentDashboardAccessScopeGroups = createSelector(
  selectCurrentDashboard,
  allGroups,
  (dashboard, groupsById) => {
    const groupIds = dashboard.access_scope_group_ids || [];
    if (groupIds.length === 0) {
      return [groupsById[dashboard.group_id]].filter(Boolean);
    }
    return groupIds.map(id => groupsById[id]).filter(Boolean);
  }
);

export const selectCurrentDashboardGroup = createSelector(
  selectCurrentDashboardGroupId,
  allGroups,
  (groupId, groupsById) => groupsById[groupId]
);

const emptyArray = [];

// Returns all dashboards for the current group
export const selectGroupDashboards = createSelector(
  selectCurrentDashboardGroupId,
  metadataApiSelectors.values,
  (groupId, accessibleDashboards) => {
    let result = accessibleDashboards.filter(d => d.group_id === groupId);
    // remove all special dashboards that have a tag
    result = result.filter(d => !d.tag);
    // avoid unnecessary rerenders if there are no results
    if (result.length === 0) return emptyArray;
    return result;
  }
);

export const selectTemplates = createSelector(
  metadataApiSelectors.values,
  selectCurrentDashboardGroupId,
  (dashboards, groupId) =>
    dashboards
      .filter(d => d.system_template)
      .filter(d => {
        // only show templates that are either applicable to all groups, or
        // applicable to the current group.
        const applicableGroupIds = d.template_applicable_group_ids || [];
        return applicableGroupIds.length === 0 || applicableGroupIds.includes(groupId);
      })
      .sort((a, b) => a.title.localeCompare(b.title))
);

export const dashboardAuthHeader = state => {
  const authToken = state.dashboards.dashboardReadAuthToken;
  if (!authToken) return {};
  return { Authorization: `Bearer ${authToken.access_token}` };
};

// returns the layout of the widgets on the active page, in the format that
// react-grid-layout requires
export const activePageWidgetCoordinates = createSelector(selectActivePage, page => {
  return page.widgets.map(widget => ({
    i: String(widget.id),
    x: widget.position_x,
    y: widget.position_y,
    h: widget.height,
    w: widget.width
  }));
});

export const selectCurrentDashboardGroupOrSubgroups = createSelector(
  selectCurrentDashboardGroup,
  allGroups,
  (group, groupsById) => {
    if (!group) return [];

    // parent_group_id is loaded for BasicGroups as well. This is a safer
    // mechanism than using descendant_ids, which is not loaded for BasicGroups.
    const subgroups = Object.values(groupsById).filter(g => {
      let parentId = g.parent_group_id;
      // walk up the hierarchy to see if this group is a descendant of the
      // current dashboard group
      while (parentId && parentId !== group.id) parentId = groupsById[parentId]?.parent_group_id;
      return parentId === group.id;
    });

    if (subgroups.length === 0) return [group];
    return sortBy(subgroups, ['display_order']);
  }
);

export default {
  ...metadataApiSelectors,
  selectDashboardDatetime,
  selectCurrentDashboard,
  selectGroupDashboards,
  selectActivePage,
  activePageWidgetCoordinates,
  selectCurrentDashboardGroup,
  selectTemplates,
  selectCurrentDashboardAccessScopeGroups,
  selectCurrentDashboardGroupOrSubgroups
};
